import { FormControl, MenuItem, Select, Grid, InputAdornment, Typography, type SelectChangeEvent } from '@mui/material';
import RequireIcon from '@mui/icons-material/FiberManualRecord';

export type Option = { value: string | number; label: string; disabled?: boolean };

type FormSelectProps<State extends Record<string, any>> = {
    label: string;
    options: Option[];
    stateKey: keyof State;
    draft: [State, React.Dispatch<React.SetStateAction<State>>];
    required?: boolean;
    defaultFirst?: boolean;
    endAdornment?: React.ReactNode;
    disabled?: boolean;
    size?: number;
};

export const FormSelect = <State extends Record<string, any>>({
    label,
    options,
    stateKey,
    draft,
    required,
    defaultFirst = true,
    disabled,
    endAdornment,
    size = 6,
}: FormSelectProps<State>) => {
    const [state, setState] = draft;

    const handleChange = (e: SelectChangeEvent<string | number>) => {
        setState((prev) => ({
            ...prev,
            [stateKey]: e.target.value,
        }));
    };

    const hasError = required && !state?.[stateKey];

    return (
        <Grid item xs={12} sm={12} md={size}>
            <Typography variant='body2' color={hasError ? 'error' : 'text.primary'} fontWeight={1000} sx={{ pl: 0.5 }}>
                {label}
            </Typography>

            <FormControl fullWidth error={hasError} size='small'>
                <Select
                    value={state?.[stateKey] ?? (defaultFirst ? options[0]?.value : '')}
                    onChange={handleChange}
                    disabled={disabled}
                    endAdornment={endAdornment}
                    startAdornment={
                        required ? (
                            <InputAdornment position='start'>
                                <RequireIcon fontSize='small' sx={{ color: 'error.main' }} />
                            </InputAdornment>
                        ) : undefined
                    }
                    sx={{
                        fontSize: '0.875rem',
                        pr: endAdornment ? '2px !important' : '10px !important',
                        '& .MuiSelect-select': {
                            py: '10px',
                        },
                    }}>
                    {options.map(({ value, label, disabled }) => (
                        <MenuItem key={value} value={value} disabled={disabled} sx={{ fontSize: '0.875rem' }}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
    );
};
